import { useState, useEffect, useRef, useCallback } from 'react'
import { verifyToken, logout as phpLogout, refreshToken } from '../lib/phpAuth'
import { User } from '../types'
import { ensureUserExists } from '../lib/authUtils'
import { TIMEOUTS } from '../lib/constants'

interface AuthUser extends User {
  email: string
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)

  const isLoadingRef = useRef(false)
  const checkIntervalRef = useRef<number | null>(null)
  const quickCheckIntervalRef = useRef<number | null>(null)
  const authenticatedRef = useRef(false) // Ref для отслеживания состояния аутентификации

  // Функция проверки и загрузки пользователя
  // Используем useRef для хранения функции, чтобы избежать проблем с зависимостями
  const checkAndLoadUserRef = useRef<() => Promise<void>>()
  
  checkAndLoadUserRef.current = async () => {
    if (isLoadingRef.current) {
      console.log('⏸️ Auth check already in progress, skipping...')
      return
    }

    isLoadingRef.current = true
    setLoading(true)

    try {
      // Session cookie (HttpOnly) — ověření přes /auth/me
      console.log('🔍 Checking session via /auth/me...')

      // Проверяем валидность токена
      const isValid = await verifyToken()
      if (!isValid) {
        console.log('❌ Token invalid')
        setUser(null)
        setAuthenticated(false)
        authenticatedRef.current = false
        setLoading(false)
        isLoadingRef.current = false
        return
      }

      console.log('✅ Token valid, loading user...')

      // Загружаем/создаем пользователя
      const userData = await ensureUserExists()

      if (userData) {
        console.log('✅✅✅ User authenticated successfully!', userData.email)
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          admin_type: userData.admin_type || undefined,
          user_type: userData.user_type || undefined,
        })
        setAuthenticated(true)
        authenticatedRef.current = true
        
        // Останавливаем интервал быстрой проверки после успешной аутентификации
        if (quickCheckIntervalRef.current !== null) {
          clearInterval(quickCheckIntervalRef.current)
          quickCheckIntervalRef.current = null
          console.log('🛑 Quick check interval stopped after successful authentication')
        }
      } else {
        console.error('❌ Failed to ensure user exists')
        setUser(null)
        setAuthenticated(false)
        authenticatedRef.current = false
      }
    } catch (error) {
      console.error('❌ Error loading user data:', error)
      setUser(null)
      setAuthenticated(false)
      authenticatedRef.current = false
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }

  const checkAndLoadUser = useCallback(() => {
    return checkAndLoadUserRef.current?.() || Promise.resolve()
  }, [])

  // ВАЖНО: Все хуки должны вызываться в правильном порядке
  // useEffect должен быть вызван всегда, независимо от условий
  useEffect(() => {
    let mounted = true
    let quickCheckInterval: number | null = null

    // Проверяем токен всегда (даже на публичных страницах), чтобы знать, авторизован ли пользователь
    // Это нужно для отображения кнопок для авторизованных пользователей (например, статистика для správce jídelny)
    // На публичных страницах не требуем авторизацию - страница доступна всем
    if (checkAndLoadUserRef.current) {
      checkAndLoadUserRef.current()
    }

    // Слушаем события обновления токена (для обновления после callback)
    const handleStorageChange = (e: StorageEvent | CustomEvent) => {
      const key = 'key' in e ? e.key : null
      if (key === 'auth_token' || e.type === 'authTokenSet') {
        console.log('🔄 Auth token changed, refreshing auth state...')
        if (mounted && checkAndLoadUserRef.current) {
          // Небольшая задержка, чтобы убедиться, что токен сохранен
          setTimeout(() => {
            if (mounted && checkAndLoadUserRef.current) {
              checkAndLoadUserRef.current()
            }
          }, 100)
        }
      }
    }

    // Добавляем слушатели для обоих типов событий
    window.addEventListener('storage', handleStorageChange as EventListener)
    window.addEventListener('authTokenSet', handleStorageChange as EventListener)
    
    // Проверяем токен периодически (каждые 2 секунды) для обновления после callback
    // Останавливаем проверку после успешной аутентификации
    // НЕ проверяем на публичных страницах, чтобы не затрагивать права пользователя
    let quickCheckAttempts = 0
    const maxQuickCheckAttempts = 30 // Максимум 30 попыток (60 секунд)
    
    quickCheckInterval = window.setInterval(() => {
      if (!mounted) return
      
      // Если уже аутентифицирован - останавливаем интервал
      if (authenticatedRef.current) {
        if (quickCheckInterval) {
          clearInterval(quickCheckInterval)
          quickCheckInterval = null
          quickCheckIntervalRef.current = null
          console.log('🛑 Quick check interval stopped - user authenticated')
        }
        return
      }
      
      quickCheckAttempts++
      
      // Если превышено количество попыток - останавливаем
      if (quickCheckAttempts > maxQuickCheckAttempts) {
        if (quickCheckInterval) {
          clearInterval(quickCheckInterval)
          quickCheckInterval = null
          quickCheckIntervalRef.current = null
        }
        return
      }
      
      // Проверяем текущее состояние аутентификации
      if (!isLoadingRef.current && checkAndLoadUserRef.current) {
        checkAndLoadUserRef.current()
      }
    }, 2000)
    
    quickCheckIntervalRef.current = quickCheckInterval

    // Функция проверки и обновления токена
    const checkTokenAndRefresh = async () => {
      if (!mounted || !authenticated) return

      try {
        const refreshed = await refreshToken()
        if (refreshed && checkAndLoadUserRef.current) {
          await checkAndLoadUserRef.current()
          return
        }

        const isValid = await verifyToken()
        if (!isValid && mounted) {
          phpLogout()
          setUser(null)
          setAuthenticated(false)
        }
      } catch (error) {
        console.error('Error checking session:', error)
      }
    }

    // Периодическая проверка токена (каждые 5 минут)
    checkIntervalRef.current = window.setInterval(() => {
      checkTokenAndRefresh()
    }, 5 * 60 * 1000)
    
    // Первоначальная проверка через 1 минуту после загрузки
    const initialCheckTimeout = setTimeout(() => {
      if (mounted && authenticated) {
        checkTokenAndRefresh()
      }
    }, 60 * 1000)

    // Таймаут для предотвращения бесконечной загрузки
    // Увеличиваем таймаут до 60 секунд для callback страницы
    const isCallbackPage = window.location.pathname === '/auth/callback'
    const timeoutDuration = isCallbackPage ? 60000 : TIMEOUTS.AUTH_LOADING
    const timeout = setTimeout(() => {
      if (mounted && !isCallbackPage) {
        // Не форсируем загрузку на странице callback - там может быть долгая обработка
        console.warn(`⏱️ Auth loading timeout (${timeoutDuration / 1000}s), forcing loading to false`)
        // НЕ сбрасываем loading сразу - даем больше времени для загрузки
        // Это предотвращает белый экран при обновлении страницы
        if (!authenticatedRef.current) {
          setLoading(false)
          isLoadingRef.current = false
        }
      }
    }, timeoutDuration)

    return () => {
      mounted = false
      window.removeEventListener('storage', handleStorageChange as EventListener)
      window.removeEventListener('authTokenSet', handleStorageChange as EventListener)
      if (quickCheckInterval !== null) {
        clearInterval(quickCheckInterval)
        quickCheckIntervalRef.current = null
      }
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current)
      }
      clearTimeout(timeout)
      clearTimeout(initialCheckTimeout)
    }
  }, []) // Пустой массив зависимостей - эффект выполняется только при монтировании

  return { user, loading, authenticated, refreshAuth: checkAndLoadUser }
}
